import React, { useEffect, useState } from 'react';
import './AssignLeadersPanel.css';
import api from '../wrapper/API';
import ActionButton from '../input/ActionButton';
import Tooltip from '../tooltip/Tooltip';
import { useSuccessBar } from '../context/SuccessBarContext';
import ProcessNotice from './ProcessNotice';
import { apiErrorMessage, eventNouns, formatDate, formatTime, isOpenEvent } from './processUtils';

/**
 * Step kind AssignLeaders: per open event toggle the leaders (GET /EventProcess/LeaderCandidates,
 * loaded once by the page) → POST Steps/{stepId}/Execute { leaders: [{ eventId, playerIds }] } (desired
 * state per event). Re-executable any time; new leaders need an Orga/Admin role at the event date.
 */
function AssignLeadersPanel({
  process, step, active, activeActions, resetAction, onStartMode, onFinished,
  leaderCandidates, ensureLeaderCandidates,
}) {
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [assignments, setAssignments] = useState(null); // { [eventId]: playerId[] }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const nouns = eventNouns(process);
  const openEvents = (process.events ?? []).filter(isOpenEvent);

  useEffect(() => {
    if (active) ensureLeaderCandidates();
  }, [active, ensureLeaderCandidates]);

  // Seed from the backend when the picker opens; afterwards only add events that are new in the
  // process (their existing leaders must not count as "deselected") and drop removed ones. Unsaved
  // chip selections of known events stay.
  useEffect(() => {
    if (!active) {
      setAssignments(null);
      setError('');
      return;
    }
    setAssignments(prev => {
      const next = {};
      (process.events ?? []).forEach(e => {
        next[e.id] = prev?.[e.id] ?? (e.leaders ?? []).map(l => l.playerId);
      });
      return next;
    });
  }, [active, process]);

  const toggleLeader = (eventId, playerId) => {
    setAssignments(prev => {
      const cur = prev?.[eventId] ?? [];
      const next = cur.includes(playerId) ? cur.filter(x => x !== playerId) : [...cur, playerId];
      return { ...prev, [eventId]: next };
    });
  };

  const handleExecute = () => {
    if (saving || !assignments) return;
    setSaving(true);
    setError('');
    startSuccessBarLoading('Spieltagleiter werden zugewiesen');
    api.post(`/EventProcess/${process.id}/Steps/${step.stepId}/Execute`, {
      leaders: openEvents.map(e => ({ eventId: e.id, playerIds: assignments[e.id] ?? [] })),
    })
      .then(response => {
        startSuccessBarSuccess('Spieltagleiter zugewiesen');
        onFinished(response.data.process, response.data.warnings);
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorMessage(err, 'Spieltagleiter konnten nicht zugewiesen werden.'));
      })
      .finally(() => setSaving(false));
  };

  if (!active) {
    const skipped = step.status === 'skipped';
    return (
      <React.Fragment>
        <p className={'processActionDesc'}>
          {skipped
            ? 'Dieser Schritt wurde übersprungen. Du kannst die Spieltagleitung trotzdem jetzt zuweisen.'
            : 'Die Spieltagleitung steht in der Tabelle oben. Du kannst sie jederzeit erneut zuweisen – neu eingetragene Leiter brauchen am Eventdatum eine Orga- oder Admin-Rolle.'}
        </p>
        <div className={'processActionButtons'}>
          <ActionButton icon={'sports'} label={skipped ? 'Jetzt zuweisen' : 'Erneut zuweisen'} type={3} onClick={() => onStartMode('run')} />
          {resetAction}
        </div>
      </React.Fragment>
    );
  }

  const candidates = leaderCandidates ?? [];
  const candidateIds = new Set(candidates.map(c => c.playerId));

  return (
    <React.Fragment>
      <p className={'processActionDesc'}>
        Weise jedem {nouns.one} die Orgas zu, die als Spieltagleiter eingeplant sind. Leiter zählen nicht zur
        Teilnehmerzahl, zahlen nichts und bekommen keine automatische Mail.
      </p>
      {error && <ProcessNotice tone={'error'}>{error}</ProcessNotice>}

      {openEvents.length === 0 ? (
        <p className={'processEmpty'}>Keine veränderbaren {nouns.many} im Prozess.</p>
      ) : leaderCandidates == null ? (
        <p className={'processEmpty'}>Orga-Kandidaten werden geladen …</p>
      ) : (
        <div className={'processLeaderPicker'}>
          {candidates.length === 0 && (
            <ProcessNotice tone={'info'}>Keine Orga-Kandidaten gefunden. Lege zuerst Orgas/Admins an.</ProcessNotice>
          )}
          {openEvents.map(e => {
            const selected = assignments?.[e.id] ?? [];
            // Current leaders without a current Orga role stay visible so they can be removed.
            const extra = (e.leaders ?? []).filter(l => !candidateIds.has(l.playerId));
            const missing = process.requiresLeader && selected.length === 0;
            return (
              <div key={e.id} className={`processLeaderRow${missing ? ' isMissing' : ''}`}>
                <div className={'processLeaderDate'}>
                  <span className={'processLeaderDatePrimary'}>{formatDate(e.date)}</span>
                  {e.time && <span className={'processLeaderTime'}>{formatTime(e.time)}</span>}
                  {e.locationName && <span className={'processLeaderLocation'}>{e.locationName}</span>}
                </div>
                <div className={'processLeaderMain'}>
                  <div className={'processLeaderChips'}>
                    {candidates.map(c => {
                      const assigned = selected.includes(c.playerId);
                      return (
                        <button
                          type={'button'}
                          key={c.playerId}
                          className={`processLeaderChip ${assigned ? 'isAssigned' : ''}`}
                          onClick={() => toggleLeader(e.id, c.playerId)}
                          aria-pressed={assigned}
                        >
                          <span className={'material-symbols-rounded processLeaderChipIcon'} aria-hidden={'true'}>sports</span>
                          {c.name}
                        </button>
                      );
                    })}
                    {extra.map(l => {
                      const assigned = selected.includes(l.playerId);
                      return (
                        <Tooltip key={l.playerId} text={'Hat aktuell keine Orga-/Admin-Rolle – kann entfernt, aber nicht neu zugewiesen werden.'}>
                          <button
                            type={'button'}
                            className={`processLeaderChip isLegacy ${assigned ? 'isAssigned' : ''}`}
                            onClick={() => toggleLeader(e.id, l.playerId)}
                            aria-pressed={assigned}
                          >
                            <span className={'material-symbols-rounded processLeaderChipIcon'} aria-hidden={'true'}>sports</span>
                            {l.name}
                          </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                  {missing && (
                    <span className={'processLeaderWarning'}>
                      <span className={'material-symbols-rounded'} aria-hidden={'true'}>warning</span>
                      Ohne Spieltagleitung bleibt das Event unsichtbar (Entwurf), auch wenn es freigegeben ist.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={'processActionButtons'}>
        <ActionButton
          icon={'sports'}
          label={'Spieltagleiter speichern'}
          type={2}
          onClick={handleExecute}
          disabled={saving || openEvents.length === 0 || leaderCandidates == null}
        />
        {activeActions}
      </div>
    </React.Fragment>
  );
}

export default AssignLeadersPanel;
